/**
 * RTWO Cryptography & Identity Engine — Domain Models
 * Phase 7 | Module ID: RTWO-CRYP-001
 * Version: 1.0.0-phase7
 *
 * Strongly typed domain models for the RTWO Cryptography & Identity layer.
 * Defines the security foundation for wallet creation, address generation,
 * transaction signing, validator identity, governance identity, and
 * secure system operations.
 *
 * Truthful Data Policy:
 * - No fake keys, addresses, signatures, identities, or trust scores.
 * - Private keys are NEVER stored or displayed — only privateKeyReference.
 * - No demo wallets, no demo identities, no fake signature validation.
 * - All runtime values are null until a real Key Provider is connected.
 *
 * Source: Cryptography & Identity Engine
 */

// ─────────────────────────────────────────────
// CRYPTOGRAPHY PRIMITIVES
// ─────────────────────────────────────────────

export type KeyPairId = string;
export type AddressId = string;
export type SignatureId = string;
export type IdentityId = string;
export type PolicyId = string;
export type TrustRecordId = string;
export type PublicKeyHex = string;
export type PrivateKeyReference = string; // NEVER the actual private key — reference only
export type AddressHex = string;
export type SignatureHex = string;
export type MessageHashHex = string;
export type ChecksumHex = string;
export type DerivationPath = string;
export type CryptoTimestamp = number | null;

export const CRYPTOGRAPHY_ENGINE_VERSION = {
  moduleId: 'RTWO-CRYP-001',
  moduleName: 'Cryptography & Identity Engine',
  version: '1.0.0-phaseA2',
  phase: 'Phase A.2 — Real Cryptography Backend',
  status: 'Ed25519, secp256k1, and SHA-256 Providers Operational — Identity Backends Pending',
  productionReadiness: 'Operational Software Key Providers — HSM/KMS/Identity Scaffolds Pending',
  policy: 'Truthful Data Policy Enforced — No Fake Keys, Addresses, or Identities',
} as const;

// ─────────────────────────────────────────────
// CRYPTOGRAPHIC ALGORITHM
// ─────────────────────────────────────────────

export type CryptoAlgorithm = 'Ed25519' | 'secp256k1' | 'secp256r1' | 'BLS12-381' | 'sr25519';

// ─────────────────────────────────────────────
// ELLIPTIC CURVE
// ─────────────────────────────────────────────

export type EllipticCurve = 'Ed25519' | 'secp256k1' | 'secp256r1' | 'BLS12-381' | 'Ristretto255';

// ─────────────────────────────────────────────
// ADDRESS TYPE
// ─────────────────────────────────────────────

export type AddressType =
  'wallet' | 'validator' | 'governance' | 'contract' | 'system' | 'multisig';

// ─────────────────────────────────────────────
// IDENTITY TYPE
// ─────────────────────────────────────────────

export type IdentityType =
  | 'wallet_owner'
  | 'validator_node'
  | 'governance_participant'
  | 'enterprise_operator'
  | 'system_service'
  | 'developer';

// ─────────────────────────────────────────────
// STORAGE TYPE
// ─────────────────────────────────────────────

export type KeyStorageType =
  'hsm' | 'encrypted_file' | 'secure_enclave' | 'kms' | 'memory_only' | 'not_configured';

// ─────────────────────────────────────────────
// TRUST LEVEL
// ─────────────────────────────────────────────

export type TrustLevel =
  'unverified' | 'self_declared' | 'peer_verified' | 'authority_verified' | 'system_verified';

// ─────────────────────────────────────────────
// TRUST SOURCE
// ─────────────────────────────────────────────

export type TrustSource =
  'self' | 'peer_network' | 'governance_authority' | 'system_registry' | 'not_configured';

// ─────────────────────────────────────────────
// KEY LIFECYCLE
// ─────────────────────────────────────────────

/**
 * Key Lifecycle:
 * Requested → Generated → Active → Rotating → Rotated → Revoked → Compromised → Archived
 */
export type KeyStatus =
  | 'requested'
  | 'generated'
  | 'active'
  | 'rotating'
  | 'rotated'
  | 'revoked'
  | 'compromised'
  | 'archived';

export interface KeyLifecycleStage {
  status: KeyStatus;
  label: string;
  description: string;
  terminal: boolean;
  requiresKeyProvider: boolean;
  securityNote: string;
}

export const KEY_LIFECYCLE: KeyLifecycleStage[] = [
  {
    status: 'requested',
    label: 'Requested',
    description: 'Key pair generation has been requested by an authorized system component.',
    terminal: false,
    requiresKeyProvider: true,
    securityNote: 'Request must originate from an authorized identity context.',
  },
  {
    status: 'generated',
    label: 'Generated',
    description:
      'Key pair has been generated by the Key Provider. Private key is stored securely — never exposed.',
    terminal: false,
    requiresKeyProvider: true,
    securityNote:
      'Private key is stored in secure storage. Only privateKeyReference is retained in system records.',
  },
  {
    status: 'active',
    label: 'Active',
    description: 'Key pair is active and authorized for signing operations.',
    terminal: false,
    requiresKeyProvider: true,
    securityNote:
      'Active keys may be used for transaction signing, identity verification, and governance.',
  },
  {
    status: 'rotating',
    label: 'Rotating',
    description:
      'Key rotation is in progress. New key pair is being generated to replace this key.',
    terminal: false,
    requiresKeyProvider: true,
    securityNote: 'During rotation, both old and new keys may be temporarily valid.',
  },
  {
    status: 'rotated',
    label: 'Rotated',
    description: 'Key has been successfully rotated. A new active key pair has replaced this key.',
    terminal: false,
    requiresKeyProvider: false,
    securityNote: 'Rotated keys are retained for historical signature verification only.',
  },
  {
    status: 'revoked',
    label: 'Revoked',
    description: 'Key has been explicitly revoked by an authorized operator or governance action.',
    terminal: true,
    requiresKeyProvider: false,
    securityNote: 'Revoked keys must not be used for any signing operation.',
  },
  {
    status: 'compromised',
    label: 'Compromised',
    description: 'Key has been flagged as potentially compromised. Immediate rotation required.',
    terminal: true,
    requiresKeyProvider: false,
    securityNote: 'CRITICAL: All operations using this key must be suspended immediately.',
  },
  {
    status: 'archived',
    label: 'Archived',
    description: 'Key has been archived for audit and historical verification purposes.',
    terminal: true,
    requiresKeyProvider: false,
    securityNote: 'Archived keys are read-only and retained for compliance and audit trails.',
  },
];

export const KEY_STATUS_LABELS: Record<KeyStatus, string> = {
  requested: 'Requested',
  generated: 'Generated',
  active: 'Active',
  rotating: 'Rotating',
  rotated: 'Rotated',
  revoked: 'Revoked',
  compromised: 'Compromised',
  archived: 'Archived',
};

export const KEY_STATUS_COLORS: Record<KeyStatus, string> = {
  requested: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
  generated: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/20',
  active: 'text-green-400 bg-green-400/10 border-green-400/20',
  rotating: 'text-amber-400 bg-amber-400/10 border-amber-400/20',
  rotated: 'text-slate-400 bg-slate-400/10 border-slate-400/20',
  revoked: 'text-orange-400 bg-orange-400/10 border-orange-400/20',
  compromised: 'text-red-500 bg-red-500/10 border-red-500/20',
  archived: 'text-slate-500 bg-slate-500/10 border-slate-500/20',
};

// ─────────────────────────────────────────────
// IDENTITY LIFECYCLE
// ─────────────────────────────────────────────

/**
 * Identity Lifecycle:
 * Created → Pending Verification → Verified → Suspended → Revoked → Archived
 */
export type IdentityStatus =
  'created' | 'pending_verification' | 'verified' | 'suspended' | 'revoked' | 'archived';

export interface IdentityLifecycleStage {
  status: IdentityStatus;
  label: string;
  description: string;
  terminal: boolean;
  requiresIdentityBackend: boolean;
}

export const IDENTITY_LIFECYCLE: IdentityLifecycleStage[] = [
  {
    status: 'created',
    label: 'Created',
    description: 'Identity record has been created and linked to a key pair and address.',
    terminal: false,
    requiresIdentityBackend: false,
  },
  {
    status: 'pending_verification',
    label: 'Pending Verification',
    description:
      'Identity is awaiting verification by the Identity Backend or governance authority.',
    terminal: false,
    requiresIdentityBackend: true,
  },
  {
    status: 'verified',
    label: 'Verified',
    description:
      'Identity has been verified by the Identity Backend. Authorized for system operations.',
    terminal: false,
    requiresIdentityBackend: true,
  },
  {
    status: 'suspended',
    label: 'Suspended',
    description:
      'Identity has been temporarily suspended pending investigation or compliance review.',
    terminal: false,
    requiresIdentityBackend: true,
  },
  {
    status: 'revoked',
    label: 'Revoked',
    description: 'Identity has been permanently revoked. All associated keys must be revoked.',
    terminal: true,
    requiresIdentityBackend: false,
  },
  {
    status: 'archived',
    label: 'Archived',
    description: 'Identity has been archived for audit and compliance purposes.',
    terminal: true,
    requiresIdentityBackend: false,
  },
];

export const IDENTITY_STATUS_LABELS: Record<IdentityStatus, string> = {
  created: 'Created',
  pending_verification: 'Pending Verification',
  verified: 'Verified',
  suspended: 'Suspended',
  revoked: 'Revoked',
  archived: 'Archived',
};

export const IDENTITY_STATUS_COLORS: Record<IdentityStatus, string> = {
  created: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
  pending_verification: 'text-amber-400 bg-amber-400/10 border-amber-400/20',
  verified: 'text-green-400 bg-green-400/10 border-green-400/20',
  suspended: 'text-orange-400 bg-orange-400/10 border-orange-400/20',
  revoked: 'text-red-500 bg-red-500/10 border-red-500/20',
  archived: 'text-slate-500 bg-slate-500/10 border-slate-500/20',
};

// ─────────────────────────────────────────────
// SIGNATURE STATUS
// ─────────────────────────────────────────────

export type SignatureStatus =
  'pending' | 'created' | 'verified' | 'invalid' | 'expired' | 'revoked';

// ─────────────────────────────────────────────
// DOMAIN MODEL 1: RTWOKeyPair
// ─────────────────────────────────────────────

/**
 * Represents a cryptographic key pair in the RTWO system.
 *
 * Security Rules:
 * - privateKeyReference is a reference identifier ONLY — never the actual private key.
 * - The actual private key is stored exclusively in the Key Provider (HSM, KMS, Secure Enclave).
 * - Private keys must NEVER appear in logs, UI, API responses, or any system record.
 */
export interface RTWOKeyPair {
  keyPairId: KeyPairId;
  publicKey: PublicKeyHex | null; // null until Key Provider generates
  privateKeyReference: PrivateKeyReference | null; // reference only — NEVER the actual key
  algorithm: CryptoAlgorithm;
  curve: EllipticCurve;
  status: KeyStatus;
  createdAt: CryptoTimestamp;
  rotatedAt: CryptoTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 2: RTWOAddress
// ─────────────────────────────────────────────

/**
 * Represents a derived blockchain address in the RTWO system.
 *
 * Truthful Data Policy:
 * - address is null until Key Provider derives from real public key.
 * - No fake addresses are generated.
 */
export interface RTWOAddress {
  address: AddressHex | null; // null until derived from real public key
  publicKeyHash: string | null; // null until Key Provider provides
  addressType: AddressType;
  derivationPath: DerivationPath | null; // null until Key Provider provides
  checksum: ChecksumHex | null; // null until derived
  status: KeyStatus;
  createdAt: CryptoTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 3: RTWOSignature
// ─────────────────────────────────────────────

/**
 * Represents a cryptographic signature in the RTWO system.
 *
 * Truthful Data Policy:
 * - signature is null until Key Provider signs with real private key.
 * - No fake signatures are generated.
 * - No fake signature validation is performed.
 */
export interface RTWOSignature {
  signatureId: SignatureId;
  messageHash: MessageHashHex | null; // null until real message is provided
  publicKey: PublicKeyHex | null; // null until Key Provider provides
  signature: SignatureHex | null; // null until Key Provider signs
  algorithm: CryptoAlgorithm;
  status: SignatureStatus;
  createdAt: CryptoTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 4: RTWOIdentity
// ─────────────────────────────────────────────

/**
 * Represents a verified identity in the RTWO system.
 *
 * Truthful Data Policy:
 * - verificationStatus is 'not_connected' until Identity Backend is connected.
 * - No fake verified identities are created.
 */
export interface RTWOIdentity {
  identityId: IdentityId;
  address: AddressHex | null; // null until address is derived
  identityType: IdentityType;
  publicKey: PublicKeyHex | null; // null until Key Provider provides
  verificationStatus: 'not_connected' | 'pending' | 'verified' | 'failed';
  status: IdentityStatus;
  createdAt: CryptoTimestamp;
  updatedAt: CryptoTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 5: RTWOKeyStoragePolicy
// ─────────────────────────────────────────────

/**
 * Defines the key storage policy for a key pair or identity.
 *
 * Security Requirements:
 * - encryptionRequired must always be true for production.
 * - rotationRequired defines mandatory rotation schedule.
 * - backupRequired ensures disaster recovery capability.
 */
export interface RTWOKeyStoragePolicy {
  policyId: PolicyId;
  storageType: KeyStorageType;
  encryptionRequired: boolean;
  rotationRequired: boolean;
  backupRequired: boolean;
  recoveryAllowed: boolean;
  status: 'defined' | 'active' | 'not_configured' | 'deprecated';
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 6: RTWOTrustRecord
// ─────────────────────────────────────────────

/**
 * Represents a trust record for an identity in the RTWO system.
 *
 * Truthful Data Policy:
 * - trustLevel is 'unverified' until Identity Backend confirms.
 * - No fake trust scores are generated.
 */
export interface RTWOTrustRecord {
  trustRecordId: TrustRecordId;
  identityId: IdentityId;
  trustLevel: TrustLevel;
  source: TrustSource;
  status: 'active' | 'revoked' | 'expired' | 'not_configured';
  createdAt: CryptoTimestamp;
}

// ─────────────────────────────────────────────
// CRYPTOGRAPHY ENGINE STATUS
// ─────────────────────────────────────────────

export type CryptoEngineStatus =
  | 'not_initialized'
  | 'structure_ready'
  | 'waiting_for_key_provider'
  | 'key_provider_connected'
  | 'operational'
  | 'error';

export const CRYPTO_ENGINE_STATUS_LABELS: Record<CryptoEngineStatus, string> = {
  not_initialized: 'Not Initialized',
  structure_ready: 'Cryptography Structure Ready',
  waiting_for_key_provider: 'Waiting for Real Key Provider',
  key_provider_connected: 'Key Provider Connected',
  operational: 'Operational',
  error: 'Error',
};

// ─────────────────────────────────────────────
// CRYPTOGRAPHY EVENT
// ─────────────────────────────────────────────

export type CryptoEventType =
  | 'key_pair_requested'
  | 'key_pair_generated'
  | 'key_activated'
  | 'key_rotation_started'
  | 'key_rotated'
  | 'key_revoked'
  | 'key_compromised'
  | 'key_archived'
  | 'address_derived'
  | 'signature_created'
  | 'signature_verified'
  | 'identity_created'
  | 'identity_verified'
  | 'identity_suspended'
  | 'identity_revoked'
  | 'trust_record_created'
  | 'trust_record_updated'
  | 'key_provider_registered'
  | 'key_provider_connected'
  | 'identity_backend_registered'
  | 'identity_backend_connected'
  | 'configuration_bound'
  | 'repositories_bootstrapped';

export interface CryptoEvent {
  eventId: string;
  eventType: CryptoEventType;
  entityId: string;
  timestamp: CryptoTimestamp;
  payload: Record<string, unknown>;
}

// ─────────────────────────────────────────────
// SERVICE IDS
// ─────────────────────────────────────────────

export const CRYPTO_SERVICE_IDS = {
  KEY_PAIR_GENERATION: 'rtwo-cryp-key-pair-generation',
  ADDRESS_DERIVATION: 'rtwo-cryp-address-derivation',
  SIGNATURE_CREATION: 'rtwo-cryp-signature-creation',
  SIGNATURE_VERIFICATION: 'rtwo-cryp-signature-verification',
  PUBLIC_KEY_VALIDATION: 'rtwo-cryp-public-key-validation',
  IDENTITY_REGISTRATION: 'rtwo-cryp-identity-registration',
  IDENTITY_VERIFICATION: 'rtwo-cryp-identity-verification',
  KEY_ROTATION: 'rtwo-cryp-key-rotation',
  KEY_STORAGE_POLICY: 'rtwo-cryp-key-storage-policy',
  TRUST_RECORD: 'rtwo-cryp-trust-record',
  EVENT_PUBLISHING: 'rtwo-cryp-event-publishing',
  PROVIDER_LIFECYCLE: 'rtwo-cryp-provider-lifecycle',
  PROVIDER_REGISTRATION: 'rtwo-cryp-provider-registration',
  CONFIGURATION_BINDING: 'rtwo-cryp-configuration-binding',
  REPOSITORY_BOOTSTRAP: 'rtwo-cryp-repository-bootstrap',
} as const;

export type CryptoRepositoryAccessStatus = 'not_configured' | 'read_only' | 'read_write';

export interface RTWOCryptoRepositoryDescriptor {
  repositoryId: string;
  repositoryName: string;
  description: string;
  accessStatus: CryptoRepositoryAccessStatus;
  recordCount: number | null;
  lastAccessedAt: CryptoTimestamp;
}

export const CRYPTO_REPOSITORY_DEFINITIONS: RTWOCryptoRepositoryDescriptor[] = [
  {
    repositoryId: 'repo-key-pairs',
    repositoryName: 'KeyPairRepository',
    description: 'Stores RTWOKeyPair records ingested from Key Provider.',
    accessStatus: 'not_configured',
    recordCount: null,
    lastAccessedAt: null,
  },
  {
    repositoryId: 'repo-addresses',
    repositoryName: 'AddressRepository',
    description: 'Stores RTWOAddress records derived from Key Provider.',
    accessStatus: 'not_configured',
    recordCount: null,
    lastAccessedAt: null,
  },
  {
    repositoryId: 'repo-signatures',
    repositoryName: 'SignatureRepository',
    description: 'Stores RTWOSignature records from signing operations.',
    accessStatus: 'not_configured',
    recordCount: null,
    lastAccessedAt: null,
  },
  {
    repositoryId: 'repo-identities',
    repositoryName: 'IdentityRepository',
    description: 'Stores RTWOIdentity records from Identity Backend.',
    accessStatus: 'not_configured',
    recordCount: null,
    lastAccessedAt: null,
  },
  {
    repositoryId: 'repo-policies',
    repositoryName: 'KeyStoragePolicyRepository',
    description: 'Stores RTWOKeyStoragePolicy definitions.',
    accessStatus: 'not_configured',
    recordCount: null,
    lastAccessedAt: null,
  },
  {
    repositoryId: 'repo-trust-records',
    repositoryName: 'TrustRecordRepository',
    description: 'Stores RTWOTrustRecord entries from Identity Backend.',
    accessStatus: 'not_configured',
    recordCount: null,
    lastAccessedAt: null,
  },
];

export type CryptoConfigurationBindingStatus = 'not_bound' | 'bound' | 'active' | 'invalid';

export interface RTWOCryptoConfigurationBinding {
  bindingId: string;
  configurationId: string;
  configurationName: string;
  targetType: 'key_provider' | 'identity_backend';
  providerType: string;
  scope: string;
  status: CryptoConfigurationBindingStatus;
  boundAt: CryptoTimestamp;
}

export const CRYPTO_CONFIGURATION_BINDINGS: Omit<
  RTWOCryptoConfigurationBinding,
  'bindingId' | 'boundAt'
>[] = [
  {
    configurationId: 'cfg-005',
    configurationName: 'Cryptography Key Provider',
    targetType: 'key_provider',
    providerType: 'not_configured',
    scope: 'cryptography-engine',
    status: 'not_bound',
  },
  {
    configurationId: 'cfg-007',
    configurationName: 'Identity Backend Provider',
    targetType: 'identity_backend',
    providerType: 'not_configured',
    scope: 'cryptography-engine.identity',
    status: 'not_bound',
  },
];

export interface Phase22ChecklistItem {
  id: string;
  label: string;
  status: 'ready' | 'pending' | 'blocked';
  note: string;
}

export const PHASE22_CRYPTO_PROVIDER_CHECKLIST: Phase22ChecklistItem[] = [
  {
    id: 'hsm-scaffold',
    label: 'HSM key provider scaffold implemented',
    status: 'ready',
    note: 'Registered — production HSM connection not implemented',
  },
  {
    id: 'kms-scaffold',
    label: 'KMS key provider scaffold implemented',
    status: 'ready',
    note: 'Registered — production KMS connection not implemented',
  },
  {
    id: 'enclave-scaffold',
    label: 'Secure Enclave key provider scaffold implemented',
    status: 'ready',
    note: 'Registered — production enclave connection not implemented',
  },
  {
    id: 'identity-scaffolds',
    label: 'Identity backend scaffolds implemented',
    status: 'ready',
    note: 'System Registry and Peer Network backends registered as scaffolds',
  },
  {
    id: 'provider-registration',
    label: 'Provider registration and catalog bootstrap',
    status: 'ready',
    note: 'Five providers registered via catalog on engine initialization',
  },
  {
    id: 'configuration-binding',
    label: 'Configuration binding service',
    status: 'ready',
    note: 'Binds cfg-005 and cfg-007 to provider selection',
  },
  {
    id: 'repository-bootstrap',
    label: 'Repository bootstrap service',
    status: 'ready',
    note: 'Bootstraps six repositories when providers are ready',
  },
  {
    id: 'real-crypto',
    label: 'Real cryptographic operations',
    status: 'ready',
    note: 'Ed25519, secp256k1, and SHA-256 providers operational when configured and connected',
  },
  {
    id: 'ed25519-provider',
    label: 'Ed25519 key provider implemented',
    status: 'ready',
    note: 'Operational key generation, signing, and verification — no pre-seeded keys',
  },
  {
    id: 'secp256k1-provider',
    label: 'secp256k1 key provider implemented',
    status: 'ready',
    note: 'Operational key generation, signing, and verification — no pre-seeded keys',
  },
  {
    id: 'sha256-hash-provider',
    label: 'SHA-256 hash provider implemented',
    status: 'ready',
    note: 'Truthful SHA-256 hashing available when configured',
  },
  {
    id: 'provider-factory',
    label: 'Cryptography provider factory',
    status: 'ready',
    note: 'Factory creates operational and scaffold providers by type',
  },
];
